import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import "./accountBillingPage.css";
import billingRequest from "../../../request/billing";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import BillingPageSkeleton from "./billingPageSkeleton/billingPageSkeleton";
import BannerMessage from "../../common/bannerMessage/bannerMessage";

const AccountBillingPage = () => {
    const {
        userData,
        language,
        setLanguage,
        category,
        setCategory,
        setShopsData,
    } = useContext(CombinedContext);
    const [billing, setBilling] = useState(null);
    const [loading, setLoading] = useState(true);
    const [referral, setReferral] = useState(null);
    const [refLoading, setRefLoading] = useState(true);
	const [banner, setBanner] = useState(null);
    const [exiting, setExiting] = useState(false);

    const [error, setError] = useState("");

    const shopId = userData.shop.shopId;

    const navigate = useNavigate();

    useEffect(() => {
        if (!shopId) {
            setError("Missing shopId");
            setLoading(false);
            return;
        }

        loadBilling();
    }, [shopId]);

    useEffect(() => {
        if (userData?.user?.userId) {
            loadReferral();
        }
    }, [userData]);

    const loadReferral = async () => {
        try {
            const res = await billingRequest.getReferralDashboard(
                userData.user.userId,
                userData.cognitoTokens.AccessToken
            );
            setReferral(res);
        } catch (err) {
            console.error("Failed to load referral dashboard:", err);
        } finally {
            setRefLoading(false);
        }
    };

    const loadBilling = async () => {
        try {
            const res = await billingRequest.getBillingInfo(
                shopId,
                userData.cognitoTokens.AccessToken
            );
            const sub = res.billing?.subscription;
            let normalizedSub = null;

            if (sub) {
                const items = (sub.items || []).map((item) => {
                    const unitPrice = item.amount
                        ? Number(item.amount) / 100
                        : 0;
                    const quantity = item.quantity ?? 1;

                    return {
                        priceId: item.priceId,
                        productName:
                            item.productName || "Subscription Sin Fila",
                        unitPrice,
                        quantity,
                        total: unitPrice * quantity,
                        currency: item.currency,
                        interval: item.interval,
                    };
                });

                const grandTotal = items.reduce((sum, i) => sum + i.total, 0);

                normalizedSub = {
                    items,
                    grandTotal: grandTotal.toFixed(2),

                    frequency: sub.billingCycle?.frequency || 1,
                    interval: sub.billingCycle?.interval || "month",

                    status: sub.status,
                    currency: sub.currency,

                    nextBillingDate: sub.nextBillingDate,
                    firstBillingDate: sub.firstBillingDate,
                    startedAt: sub.startedAt,

                    managementUrls: {
                        update_payment_method:
                            sub.managementUrls?.update_payment_method,
                        cancel: sub.managementUrls?.cancel,
                    },
                };
            }

            setBilling({
                ...res.billing,
                subscription: normalizedSub,
            });
        } catch (err) {
            console.error("Failed to load billing info:", err);
            setError(translations[language].billing.errorLoading);
        } finally {
            setLoading(false);
        }
    };

    const startCheckout = async () => {
        try {
            setLoading(true);

            const res = await billingRequest.billingSetup({
                shopId,
                seats: 1, // or however many you want
                userId: userData.user.userId,
                accessToken: userData.cognitoTokens.AccessToken,
            });

            if (!res.checkoutUrl) {
                throw new Error("Backend did not return checkout URL.");
            }

            window.location.href = res.checkoutUrl;
        } catch (err) {
            console.error(err);
            showBanner("error", translations[language].billing.errorCheckout || "Failed to start checkout");
        } finally {
            setLoading(false);
        }
    };

    const generateCode = async () => {
        try {
            const res = await billingRequest.generateReferralCode({
                userId: userData.user.userId,
                accessToken: userData.cognitoTokens.AccessToken,
            });

            // reload dashboard after generating
            loadReferral();
        } catch (err) {
            console.error("Failed to generate referral code:", err);
            showBanner("error", translations[language].billing.referral.errorGenerate || "Unable to generate referral code");
        }
    };

	const showBanner = (type, text, duration = 2000) => {
		setBanner({ type, text });
		setExiting(false);
	
		setTimeout(() => {
			setExiting(true);
		}, duration - 250); // start exit before removal
	
		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	const copyReferralCode = async () => {
		try {
			await navigator.clipboard.writeText(referral.referralCode);
			showBanner("success", "Copied to clipboard");
		} catch (err) {
			showBanner("error", "Failed to copy");
		}
	};
	
	const copyReferralLink = async () => {
		const link = `https://reservasinfila.com/listyourshop/${referral.referralCode}`;
	
		try {
			await navigator.clipboard.writeText(link);
			showBanner("success", "Referral link copied");
		} catch (err) {
			showBanner("error", "Failed to copy link");
		}
	};
	
    if (loading) {
        return <BillingPageSkeleton />;
    }

    if (error) {
        return (
            <div className="sf-billingpage">
                <div className="sf-billing-main">
                    <p className="sf-billing-error">{error}</p>
                </div>
            </div>
        );
    }

    const subscription = billing?.subscription || null;
    const invoices = billing?.invoices || [];

    const plans = billing?.plans || [
        {
            id: "starter",
            label: "Starter",
            description: "Perfect for solo barbers or stylists.",
            priceMonthly: 9,
            currency: "USD",
            priceId: "pri_STARTER_MONTHLY",
        },
    ];

    return (
        <div className="sf-billingpage">
            {/* MAIN */}

            <div className="sf-billing-main">
                {/* Header */}
                {/* <div className="sf-billing-header-row">
                    <button
                        className="sf-back-button"
                        onClick={() => navigate(-1)}
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <div className="sf-billing-title">
                        {translations[language].billing.title}
                    </div>
                </div>
				 */}

				

                <div className="sf-billing-content">
                    {/* Subscription Card */}
                    <div className="sf-billing-main-part">
                        <div className="sf-billing-card sf-sidebar-card">
                            <h2 className="sf-section-title">
                                {
                                    translations[language].billing
                                        .currentSubscription
                                }
                            </h2>

                            {subscription ? (
                                <>
                                    <div className="sf-sub-row">
                                        <div>
                                            {subscription?.items?.map(
                                                (item, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="sf-sub-item"
                                                    >
                                                        <div className="sf-sub-plan">
                                                            {item.productName}
                                                        </div>

                                                        <div className="sf-sub-unit">
                                                            {item.quantity} ×{" "}
                                                            {
                                                                subscription.currency
                                                            }{" "}
                                                            {item.unitPrice.toFixed(
                                                                2
                                                            )}
                                                        </div>

                                                        <div className="sf-sub-item-total">
                                                            {
                                                                subscription.currency
                                                            }{" "}
                                                            {item.total.toFixed(
                                                                2
                                                            )}
                                                            /{item.interval}
                                                        </div>
                                                    </div>
                                                )
                                            )}

                                            <div className="sf-sub-status">
                                                <span
                                                    className={`sf-badge sf-badge-${subscription.status}`}
                                                >
                                                    {
                                                        translations[language]
                                                            .billing
                                                            .statusBadge[
                                                            subscription.status
                                                        ]
                                                    }
                                                </span>
                                            </div>

                                            {subscription.nextBillingDate && (
                                                <div className="sf-sub-next">
                                                    {translations[
                                                        language
                                                    ].billing.nextBillingOn.replace(
                                                        "{date}",
                                                        new Date(
                                                            subscription.nextBillingDate
                                                        ).toLocaleDateString()
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="sf-sub-total">
                                            <strong>Total:</strong>{" "}
                                            {subscription.currency}{" "}
                                            {subscription.grandTotal}/
                                            {subscription.interval}
                                        </div>

                                        <div className="sf-sub-actions">
                                            <button
                                                className="sf-btn sf-btn-outline"
                                                onClick={() =>
                                                    window.open(
                                                        subscription
                                                            .managementUrls
                                                            ?.update_payment_method,
                                                        "_blank"
                                                    )
                                                }
                                            >
                                                {
                                                    translations[language]
                                                        .billing.buttons
                                                        .updatePaymentMethod
                                                }
                                            </button>

                                            <button
                                                className="sf-btn sf-btn-warning"
                                                onClick={() =>
                                                    window.open(
                                                        subscription
                                                            .managementUrls
                                                            ?.cancel,
                                                        "_blank"
                                                    )
                                                }
                                            >
                                                {
                                                    translations[language]
                                                        .billing.buttons
                                                        .cancelSubscription
                                                }
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="sf-empty">
                                    <p>
                                        {
                                            translations[language].billing
                                                .noSubscription
                                        }
                                    </p>
                                    <button
                                        className="sf-btn sf-btn-primary"
                                        onClick={() =>
                                            startCheckout(plans[0].priceId)
                                        }
                                    >
                                        {
                                            translations[language].billing
                                                .startSubscription
                                        }
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="sf-billing-card sf-sidebar-card">
							<h3 className="sf-sidebar-title">{translations[language].billing.referral.title}</h3>

							{refLoading ? (
								<div className="sf-empty-small">Loading...</div>
							) : referral?.referralCode ? (
								<>
									<div className="sf-referral-line">
										<span className="sf-info-label">{translations[language].billing.referral.codeLabel}</span>
										<span className="sf-referral-code">{referral.referralCode}</span>
									</div>

									<button className="sf-btn sf-btn-outline" onClick={() => navigator.clipboard.writeText(referral.referralCode)}>
										{translations[language].billing.referral.copyCode}
									</button>

									{/* Referral history (optional) */}
									{Array.isArray(referral.referrals) && referral.referrals.length > 0 ? (
										<div style={{ marginTop: 12 }}>
											<h4 className="sf-sidebar-subtitle">{translations[language].billing.referral.historyTitle}</h4>
											{referral.referrals.map((r, i) => (
												<div key={i} className="sf-referral-history-item">
													<span>{r.invitedShopName || "New Shop"}</span>
													<span className={`sf-badge sf-badge-${r.status || "pending"}`}>{r.status || "pending"}</span>
												</div>
											))}
										</div>
									) : (
										<div className="sf-empty-small">{translations[language].billing.referral.noReferrals}</div>
									)}
								</>
							) : (
								<>
									<div className="sf-empty-small">{translations[language].billing.referral.noCode}</div>
									<button className="sf-btn sf-btn-primary" onClick={generateCode}>
										{translations[language].billing.referral.generate}
									</button>
								</>
							)}
						</div>

                    </div>
                    {/* SIDEBAR */}
                    <div className="sf-billing-sidebar">
                        {/* Payment Method */}
                        <div className="sf-billing-card sf-sidebar-card">
                            <h3 className="sf-sidebar-title">
                                {
                                    translations[language].billing.paymentMethod
                                        .title
                                }
                            </h3>

                            {billing?.customer?.paymentMethod ? (
                                <div className="sf-payment-method-card">
                                    <div className="sf-payment-method-line">
                                        <span className="sf-payment-label">
                                            {
                                                translations[language].billing
                                                    .paymentMethod.brand
                                            }
                                        </span>
                                        <span>
                                            {
                                                billing.customer.paymentMethod
                                                    .brand
                                            }
                                        </span>
                                    </div>

                                    <div className="sf-payment-method-line">
                                        <span className="sf-payment-label">
                                            {
                                                translations[language].billing
                                                    .paymentMethod.last4
                                            }
                                        </span>
                                        <span>
                                            ••••{" "}
                                            {
                                                billing.customer.paymentMethod
                                                    .last4
                                            }
                                        </span>
                                    </div>

                                    <div className="sf-payment-method-line">
                                        <span className="sf-payment-label">
                                            {
                                                translations[language].billing
                                                    .paymentMethod.expires
                                            }
                                        </span>
                                        <span>
                                            {
                                                billing.customer.paymentMethod
                                                    .expMonth
                                            }
                                            /
                                            {
                                                billing.customer.paymentMethod
                                                    .expYear
                                            }
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="sf-empty-small">
                                    {
                                        translations[language].billing
                                            .paymentMethod.noStored
                                    }
                                </div>
                            )}
                        </div>

                        {/* Billing Dates */}
                        {subscription && (
                            <div className="sf-billing-card sf-sidebar-card">
                                <h3 className="sf-sidebar-title">
                                    {
                                        translations[language].billing
                                            .billingInfo.title
                                    }
                                </h3>

                                <div className="sf-info-line">
                                    <span className="sf-info-label">
                                        {
                                            translations[language].billing
                                                .billingInfo.started
                                        }
                                    </span>
                                    <span>
                                        {new Date(
                                            subscription.startedAt
                                        ).toLocaleDateString()}
                                    </span>
                                </div>

                                <div className="sf-info-line">
                                    <span className="sf-info-label">
                                        {
                                            translations[language].billing
                                                .billingInfo.firstBilled
                                        }
                                    </span>
                                    <span>
                                        {new Date(
                                            subscription.firstBillingDate
                                        ).toLocaleDateString()}
                                    </span>
                                </div>

                                <div className="sf-info-line">
                                    <span className="sf-info-label">
                                        {
                                            translations[language].billing
                                                .billingInfo.nextBilling
                                        }
                                    </span>
                                    <span>
                                        {new Date(
                                            subscription.nextBillingDate
                                        ).toLocaleDateString()}
                                    </span>
                                </div>

                                <div className="sf-info-line">
                                    <span className="sf-info-label">
                                        {
                                            translations[language].billing
                                                .billingInfo.interval
                                        }
                                    </span>
                                    <span>
                                        {translations[
                                            language
                                        ].billing.billingInterval
                                            .replace(
                                                "{frequency}",
                                                subscription.frequency
                                            )
                                            .replace(
                                                "{interval}",
                                                subscription.interval
                                            )}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
			{banner && (
                <BannerMessage
                    type={banner.type}
                    text={banner.text}
                    exiting={exiting}
                />
            )}

			
        </div>
    );
};

export default AccountBillingPage;
